import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/********************************************************************************
 * Copyright (c) 2019 TypeFox and others
 *
 * This program and the accompanying materials are made available under the
 * terms of the Eclipse Public License v. 2.0 which is available at
 * http://www.eclipse.org/legal/epl-2.0.
 *
 * SPDX-License-Identifier: EPL-2.0
 ********************************************************************************/
import { useCallback, useContext, useMemo, useRef, useState } from 'react';
import { Avatar, Box, IconButton, Link, Menu, MenuItem, Typography } from '@mui/material';
import { Link as RouteLink } from 'react-router';
import SettingsIcon from '@mui/icons-material/Settings';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import LogoutIcon from '@mui/icons-material/Logout';
import { UserSettingsRoutes } from './user-settings-routes';
import { AdminDashboardRoutes } from '../admin-dashboard/admin-dashboard-routes';
import { hasAnyAdminAccess } from '../../permissions';
import { MainContext } from '../../context';
import { LogoutForm } from './logout';
// Radius, font and min-height come from the MuiMenuItem theme override.
const menuItemSx = {
    py: '0.5rem',
    px: '0.625rem',
    gap: '0.625rem',
    color: 'text.primary',
    display: 'flex',
    alignItems: 'center'
};
const iconSx = { fontSize: '1.0625rem', color: 'text.disabled', flexShrink: 0 };
export const UserAvatar = () => {
    const [open, setOpen] = useState(false);
    const close = useCallback(() => setOpen(false), []);
    // Stable identity: a new component type each render would remount contributed entries,
    // refetching whatever they load.
    const MenuEntry = useMemo(() => {
        const Entry = ({ to, icon: Icon, children }) => (_jsxs(MenuItem, { component: RouteLink, to: to, onClick: close, sx: { ...menuItemSx, textDecoration: 'none' }, children: [_jsx(Icon, { sx: iconSx }), children] }));
        Entry.displayName = 'UserMenuEntry';
        return Entry;
    }, [close]);
    const context = useContext(MainContext);
    const { userMenuContent: UserMenuContent } = context.pageSettings.elements;
    const anchorRef = useRef(null);
    const logoutFormRef = useRef(null);
    const user = context.user;
    if (!user)
        return null;
    const initials = user.loginName.slice(0, 2).toUpperCase();
    return (_jsxs(_Fragment, { children: [_jsx(IconButton, { ref: anchorRef, title: `Logged in as ${user.loginName}`, "aria-label": 'User menu', onClick: () => setOpen(true), sx: { p: '0.3125rem' }, children: _jsx(Avatar, { src: user.avatarUrl, alt: user.loginName, sx: {
                        width: 32,
                        height: 32,
                        bgcolor: 'accentSoft',
                        color: 'secondary.light',
                        fontSize: '0.75rem',
                        fontWeight: 700,
                        borderRadius: 1
                    }, children: initials }) }), _jsxs(Menu, { open: open, anchorEl: anchorRef.current, anchorOrigin: { vertical: 'bottom', horizontal: 'right' }, transformOrigin: { vertical: 'top', horizontal: 'right' }, onClose: () => setOpen(false), children: [_jsxs(Box, { sx: { px: '0.875rem', py: '0.875rem', display: 'flex', alignItems: 'center', gap: '0.75rem' }, children: [_jsx(Avatar, { src: user.avatarUrl, sx: {
                                    width: 40,
                                    height: 40,
                                    bgcolor: 'accentSoft',
                                    color: 'secondary.light',
                                    fontSize: '0.9375rem',
                                    fontWeight: 700,
                                    borderRadius: 1,
                                    flexShrink: 0
                                }, children: initials }), _jsxs(Box, { sx: { minWidth: 0 }, children: [_jsx(Typography, { sx: {
                                            fontSize: '0.6875rem',
                                            fontWeight: 700,
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.07em',
                                            color: 'text.disabled',
                                            lineHeight: 1.3
                                        }, children: "Logged in as" }), _jsx(Typography, { sx: {
                                            fontSize: '0.875rem',
                                            fontWeight: 700,
                                            lineHeight: 1.3,
                                            color: 'text.primary',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap'
                                        }, children: _jsx(Link, { href: user.homepage, target: '_blank', onClick: () => setOpen(false), children: user.loginName }) })] })] }), _jsxs(MenuItem, { component: RouteLink, to: UserSettingsRoutes.PROFILE, onClick: () => setOpen(false), sx: { ...menuItemSx, textDecoration: 'none' }, children: [_jsx(SettingsIcon, { sx: iconSx }), "Settings"] }), UserMenuContent ? _jsx(UserMenuContent, { close: close, MenuEntry: MenuEntry }) : null, hasAnyAdminAccess(user) && (_jsxs(MenuItem, { component: RouteLink, to: AdminDashboardRoutes.MAIN, onClick: () => setOpen(false), sx: { ...menuItemSx, textDecoration: 'none' }, children: [_jsx(AdminPanelSettingsIcon, { sx: iconSx }), "Admin Dashboard"] })), _jsx(MenuItem, { onClick: () => logoutFormRef.current?.submit(), sx: menuItemSx, children: _jsxs(LogoutForm, { ref: logoutFormRef, children: [_jsx(LogoutIcon, { sx: iconSx }), "Log out"] }) })] })] }));
};
//# sourceMappingURL=avatar.js.map